import { createContext, useContext, useReducer, type ReactNode } from "react";
import type {
  Tukipyynto,
  Kayttaja,
  AuditEntry,
  Dokumentti,
  TilaMuutos,
  TukipyyntoTila,
  Virkailija,
} from "../types";
import type { RooliProfiili } from "../utils/oikeudet";
import { ADMIN_PROFIILI } from "../utils/oikeudet";

/* Demo-rooli (RooliProfiili) säilytetään sessionStoragessa JSON-muodossa.
   Tab-kohtainen. CLAUDE.md sallii sessionStoragen kun proto tarvitsee. */
const ROOLI_STORAGE_KEY = "dfau-demo-rooli";

function lueRooliStoragesta(): RooliProfiili {
  if (typeof window === "undefined") return ADMIN_PROFIILI;
  try {
    const json = window.sessionStorage.getItem(ROOLI_STORAGE_KEY);
    if (json) {
      const parsed = JSON.parse(json) as RooliProfiili;
      // Perusvalidointi: tarkista että rakenne on oikea
      if (
        parsed &&
        typeof parsed.perustyo === "string" &&
        parsed.lisaoikeudet &&
        typeof parsed.lisaoikeudet.raportit === "boolean" &&
        typeof parsed.lisaoikeudet.myonto === "boolean"
      ) {
        return parsed;
      }
    }
  } catch {
    // ignore
  }
  return ADMIN_PROFIILI;
}

function tallennaRooli(profiili: RooliProfiili) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(ROOLI_STORAGE_KEY, JSON.stringify(profiili));
  } catch {
    // ignore
  }
}

/* Lisää kasittelyhistoriaan uuden tilamuutos-rivin (workshop 1.6.2026 C1).
   Käytetään storen tukipyyntö-actioneista. */
function lisaaHistoriaan(
  tp: Tukipyynto,
  tila: TukipyyntoTila,
  virkailijaUid?: string,
  virkailijaNimi?: string,
): TilaMuutos[] {
  const rivi: TilaMuutos = { tila, aikaleima: new Date().toISOString() };
  if (virkailijaUid && virkailijaNimi) {
    rivi.virkailijaUid = virkailijaUid;
    rivi.virkailijaNimi = virkailijaNimi;
  }
  return [...tp.kasittelyhistoria, rivi];
}

interface AppState {
  tukipyynnot: Tukipyynto[];
  kayttajat: Kayttaja[];
  /** Virkailijat — käyttöoikeusnäkymän hallinnoima lista (K5 3c). */
  virkailijat: Virkailija[];
  /** Demottava rooliprofiili (K5: perustyö + lisäoikeudet). Ks. src/utils/oikeudet.ts. */
  rooli: RooliProfiili;
}

type Action =
  | {
      type: "UPDATE_TUKIPYYNTO_TILA";
      id: string;
      tila: TukipyyntoTila;
      kasittelijaUid: string;
      kasittelijaNimi: string;
    }
  | {
      type: "OTA_ITSELLE";
      tukipyyntoId: string;
      virkailijaUid: string;
      virkailijaNimi: string;
    }
  | {
      type: "OTA_KAIKKI_ITSELLE";
      kayttajaUid: string;
      virkailijaUid: string;
      virkailijaNimi: string;
    }
  | { type: "PALAUTA_AVOIMIIN"; tukipyyntoId: string }
  | {
      type: "VAIHDA_KASITTELIJA";
      tukipyyntoId: string;
      virkailijaUid: string;
      virkailijaNimi: string;
    }
  | {
      type: "LAHETA_JA_RATKAISE";
      tukipyyntoId: string;
      vastaus: string;
      virkailijaUid: string;
      virkailijaNimi: string;
    }
  | {
      type: "LAHETA_JA_RATKAISE_KAIKKI";
      kayttajaUid: string;
      vastaus: string;
      virkailijaUid: string;
      virkailijaNimi: string;
    }
  | { type: "ADD_AUDIT_ENTRY"; kayttajaUid: string; entry: AuditEntry }
  | { type: "VAPAUTA_LUKITUS"; kayttajaUid: string; entry: AuditEntry }
  | { type: "LUKITSE_KAYTTAJATUNNUS"; kayttajaUid: string; entry: AuditEntry }
  | { type: "VAPAUTA_KAYTTAJATUNNUS"; kayttajaUid: string; entry: AuditEntry }
  | { type: "TODENNA_HENKILOLLISYYS"; kayttajaUid: string; entry: AuditEntry }
  | {
      type: "TODENNA_MANUAALIPROSESSILLA";
      kayttajaUid: string;
      etunimi: string;
      sukunimi: string;
      dokumentti: Dokumentti;
      entry: AuditEntry;
    }
  | { type: "POISTA_KAYTTAJA"; kayttajaUid: string; entry: AuditEntry }
  | { type: "UPDATE_MUISTIINPANO_STICKY"; kayttajaUid: string; teksti: string }
  | { type: "SET_ROOLI"; rooli: RooliProfiili }
  | { type: "OHITA_PROOFING"; kayttajaUid: string; entry: AuditEntry }
  | { type: "LISAA_VIRKAILIJA"; virkailija: Virkailija }
  | {
      type: "PAIVITA_VIRKAILIJAN_ROOLI";
      uid: string;
      rooli: string;
      muokannutUid: string;
    }
  | { type: "POISTA_VIRKAILIJA"; uid: string }
  | { type: "SET_TUKIPYYNNOT"; tukipyynnot: Tukipyynto[] }
  | { type: "SET_KAYTTAJAT"; kayttajat: Kayttaja[] }
  | { type: "SET_VIRKAILIJAT"; virkailijat: Virkailija[] };

function reducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case "UPDATE_TUKIPYYNTO_TILA":
      return {
        ...state,
        tukipyynnot: state.tukipyynnot.map((t) =>
          t.id === action.id
            ? {
                ...t,
                tila: action.tila,
                kasittelijaUid: action.kasittelijaUid,
                kasittelijaNimi: action.kasittelijaNimi,
                paivitetty: new Date().toISOString(),
                kasittelyhistoria: lisaaHistoriaan(
                  t,
                  action.tila,
                  action.kasittelijaUid,
                  action.kasittelijaNimi,
                ),
              }
            : t,
        ),
      };
    case "OTA_ITSELLE":
      // Käsittelijä asettuu kirjautuneeksi virkailijaksi, tila → käsittelyssä.
      return {
        ...state,
        tukipyynnot: state.tukipyynnot.map((t) =>
          t.id === action.tukipyyntoId
            ? {
                ...t,
                kasittelijaUid: action.virkailijaUid,
                kasittelijaNimi: action.virkailijaNimi,
                tila: "käsittelyssä",
                paivitetty: new Date().toISOString(),
                kasittelyhistoria: lisaaHistoriaan(
                  t,
                  "käsittelyssä",
                  action.virkailijaUid,
                  action.virkailijaNimi,
                ),
              }
            : t,
        ),
      };
    case "OTA_KAIKKI_ITSELLE":
      // Kaikki käyttäjän avoimet/käsittelyssä-tilaiset pyynnöt → kirjautuneen
      // virkailijan käsittelyyn (workshop 1.6.2026 C3: laajennettu sisältämään
      // käsittelyssä-tilaiset, ei vain avoimia).
      return {
        ...state,
        tukipyynnot: state.tukipyynnot.map((t) =>
          t.kayttajaUid === action.kayttajaUid &&
          (t.tila === "avoin" || t.tila === "käsittelyssä")
            ? {
                ...t,
                kasittelijaUid: action.virkailijaUid,
                kasittelijaNimi: action.virkailijaNimi,
                tila: "käsittelyssä",
                paivitetty: new Date().toISOString(),
                kasittelyhistoria: lisaaHistoriaan(
                  t,
                  "käsittelyssä",
                  action.virkailijaUid,
                  action.virkailijaNimi,
                ),
              }
            : t,
        ),
      };
    case "PALAUTA_AVOIMIIN":
      return {
        ...state,
        tukipyynnot: state.tukipyynnot.map((t) =>
          t.id === action.tukipyyntoId
            ? {
                ...t,
                kasittelijaUid: null,
                kasittelijaNimi: null,
                tila: "avoin",
                paivitetty: new Date().toISOString(),
                kasittelyhistoria: lisaaHistoriaan(t, "avoin"),
              }
            : t,
        ),
      };
    case "VAIHDA_KASITTELIJA":
      // Käsittelijä vaihtuu toiseen virkailijaan. Tila pysyy käsittelyssä-tilassa.
      return {
        ...state,
        tukipyynnot: state.tukipyynnot.map((t) =>
          t.id === action.tukipyyntoId
            ? {
                ...t,
                kasittelijaUid: action.virkailijaUid,
                kasittelijaNimi: action.virkailijaNimi,
                tila: "käsittelyssä",
                paivitetty: new Date().toISOString(),
                kasittelyhistoria: lisaaHistoriaan(
                  t,
                  "käsittelyssä",
                  action.virkailijaUid,
                  action.virkailijaNimi,
                ),
              }
            : t,
        ),
      };
    case "LAHETA_JA_RATKAISE":
      // Viesti lähetetty (mock) ja tukipyyntö merkitty ratkaistuksi.
      // Vastausviesti tallennetaan tukipyyntöön (workshop 3.6.2026).
      return {
        ...state,
        tukipyynnot: state.tukipyynnot.map((t) =>
          t.id === action.tukipyyntoId
            ? {
                ...t,
                tila: "ratkaistu",
                kasittelijaUid: action.virkailijaUid,
                kasittelijaNimi: action.virkailijaNimi,
                paivitetty: new Date().toISOString(),
                vastaus: action.vastaus,
                kasittelyhistoria: lisaaHistoriaan(
                  t,
                  "ratkaistu",
                  action.virkailijaUid,
                  action.virkailijaNimi,
                ),
              }
            : t,
        ),
      };
    case "LAHETA_JA_RATKAISE_KAIKKI":
      // Workshop 1.6.2026 C5: kaikki saman käyttäjän avoimet/käsittelyssä
      // pyynnöt ratkaistaan samalla vastausviestillä yhdessä päätöksessä.
      return {
        ...state,
        tukipyynnot: state.tukipyynnot.map((t) =>
          t.kayttajaUid === action.kayttajaUid &&
          (t.tila === "avoin" || t.tila === "käsittelyssä")
            ? {
                ...t,
                tila: "ratkaistu",
                kasittelijaUid: action.virkailijaUid,
                kasittelijaNimi: action.virkailijaNimi,
                paivitetty: new Date().toISOString(),
                vastaus: action.vastaus,
                kasittelyhistoria: lisaaHistoriaan(
                  t,
                  "ratkaistu",
                  action.virkailijaUid,
                  action.virkailijaNimi,
                ),
              }
            : t,
        ),
      };
    case "ADD_AUDIT_ENTRY":
      return {
        ...state,
        kayttajat: state.kayttajat.map((k) =>
          k.uid === action.kayttajaUid
            ? { ...k, auditTrail: [action.entry, ...k.auditTrail] }
            : k,
        ),
      };
    case "VAPAUTA_LUKITUS":
      return {
        ...state,
        kayttajat: state.kayttajat.map((k) =>
          k.uid === action.kayttajaUid
            ? {
                ...k,
                lukittu: false,
                lukitusVapautuu: null,
                auditTrail: [action.entry, ...k.auditTrail],
              }
            : k,
        ),
      };
    case "LUKITSE_KAYTTAJATUNNUS":
      return {
        ...state,
        kayttajat: state.kayttajat.map((k) =>
          k.uid === action.kayttajaUid
            ? {
                ...k,
                kayttajatunnusLukittu: true,
                auditTrail: [action.entry, ...k.auditTrail],
              }
            : k,
        ),
      };
    case "VAPAUTA_KAYTTAJATUNNUS":
      return {
        ...state,
        kayttajat: state.kayttajat.map((k) =>
          k.uid === action.kayttajaUid
            ? {
                ...k,
                kayttajatunnusLukittu: false,
                auditTrail: [action.entry, ...k.auditTrail],
              }
            : k,
        ),
      };
    case "TODENNA_HENKILOLLISYYS":
      return {
        ...state,
        kayttajat: state.kayttajat.map((k) =>
          k.uid === action.kayttajaUid
            ? {
                ...k,
                henkilollisyysTodennettu: true,
                auditTrail: [action.entry, ...k.auditTrail],
              }
            : k,
        ),
      };
    case "TODENNA_MANUAALIPROSESSILLA":
      // Manuaaliprosessi: virkailija syöttää passi-/henkilökorttitiedot käsin.
      // Käyttäjä saa nimen, asiakirjan, ja henkilöllisyys merkitään todennetuksi.
      // manuaaliprosessillaLuotu-flag säilyy tämän jälkeen pysyvästi.
      return {
        ...state,
        kayttajat: state.kayttajat.map((k) =>
          k.uid === action.kayttajaUid
            ? {
                ...k,
                henkilollisyysTodennettu: true,
                manuaaliprosessillaLuotu: true,
                etunimi: action.etunimi,
                sukunimi: action.sukunimi,
                dokumentit: [action.dokumentti, ...k.dokumentit],
                auditTrail: [action.entry, ...k.auditTrail],
              }
            : k,
        ),
      };
    case "POISTA_KAYTTAJA":
      return {
        ...state,
        kayttajat: state.kayttajat.map((k) =>
          k.uid === action.kayttajaUid
            ? {
                ...k,
                poistettu: true,
                auditTrail: [action.entry, ...k.auditTrail],
              }
            : k,
        ),
      };
    case "UPDATE_MUISTIINPANO_STICKY":
      return {
        ...state,
        kayttajat: state.kayttajat.map((k) =>
          k.uid === action.kayttajaUid
            ? { ...k, muistiinpanoSticky: action.teksti }
            : k,
        ),
      };
    case "SET_ROOLI":
      tallennaRooli(action.rooli);
      return { ...state, rooli: action.rooli };
    case "LISAA_VIRKAILIJA":
      return {
        ...state,
        virkailijat: [...state.virkailijat, action.virkailija],
      };
    case "PAIVITA_VIRKAILIJAN_ROOLI":
      return {
        ...state,
        virkailijat: state.virkailijat.map((v) =>
          v.uid === action.uid
            ? {
                ...v,
                rooli: action.rooli,
                muokannutUid: action.muokannutUid,
                muokattuAika: new Date().toISOString(),
              }
            : v,
        ),
      };
    case "POISTA_VIRKAILIJA":
      /* K5: virkailija + käyttöoikeudet poistettu. Tila → poistettu,
         poistopäivä talteen. Säilytysaika DVV:n vahvistettavana. */
      return {
        ...state,
        virkailijat: state.virkailijat.map((v) =>
          v.uid === action.uid
            ? {
                ...v,
                tila: "poistettu",
                poistettuAika: new Date().toISOString(),
              }
            : v,
        ),
      };
    case "OHITA_PROOFING":
      /* K5 3g: Taikanappi — asettaa henkilollisyysTodennettu = true
         virkailijan päätöksellä. Audit trail kirjataan entry-kentästä.
         Vain admin/käsittelijä (ks. oikeudet.ts kayttajat.taikanappi). */
      return {
        ...state,
        kayttajat: state.kayttajat.map((k) =>
          k.uid === action.kayttajaUid
            ? {
                ...k,
                henkilollisyysTodennettu: true,
                auditTrail: [...k.auditTrail, action.entry],
              }
            : k,
        ),
      };
    default:
      return state;
  }
}

const AppContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<Action>;
} | null>(null);

/* Sticky-muistiinpanon alustus: yhdistetään olemassaolevat
   Muistiinpano[]-merkinnät yhdeksi merkkijonoksi (newline-erotettuna)
   jokaiselle käyttäjälle. Workshop 1.6.2026 -päätöksen mukaan muistiinpano
   on yksi sticky-tekstilaatikko per käyttäjä, ei chat-tyylinen lista.
   Yksityiskohdat: docs/paatokset/2026-06-01_muistiinpano-yksinkertaistus.md */
function alustaMuistiinpanot(kayttajat: Kayttaja[]): Kayttaja[] {
  return kayttajat.map((k) => ({
    ...k,
    muistiinpanoSticky: k.muistiinpanot?.map((m) => m.teksti).join("\n\n"),
  }));
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, {
    // K6: tukipyynnöt, käyttäjät JA virkailijat haetaan backendista
    // (RTK Query → SET_*). Alustetaan tyhjiksi; BackendHydraatio täyttää
    // fetchien valmistuttua.
    tukipyynnot: [],
    kayttajat: [],
    virkailijat: [],
    rooli: lueRooliStoragesta(),
  });
  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
}

export function useAppState() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useAppState must be used within AppProvider");
  return ctx;
}
